import {Box, Typography, Grid, Stack} from '@mui/material';
import './GroundTruthEvaluation.scss';

import {Fragment} from 'react/jsx-runtime';
import {t} from 'i18next';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {ReactComponent as ArrowDivider} from '/src/assets/images/arrow-background.svg';
import {useGetGroundTruthEvaluation} from '../../../../hooks/ai/review/useReview';
import {IConfusionMatrix, IMetrics} from '../../../../api/types/aiReview.type';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {numberFormatter} from '../../../../utils/utils';
const confusionMatrix: {
  label: string;
  key: keyof IConfusionMatrix;
  colorClass: string;
  tooltipTitle: string;
}[] = [
  {
    label: t('ai.review.groundTruthEvaluation.metrics.truePositives'),
    key: 'truePositives',
    colorClass: 'metric-color-teal',
    tooltipTitle: t(
      'ai.review.groundTruthEvaluation.metrics.truePositivesTooltipText'
    ),
  },
  {
    label: t('ai.review.groundTruthEvaluation.metrics.falsePositives'),
    key: 'falsePositives',
    colorClass: 'metric-color-yellow',
    tooltipTitle: t(
      'ai.review.groundTruthEvaluation.metrics.falsePositivesTooltipText'
    ),
  },
  {
    label: t('ai.review.groundTruthEvaluation.metrics.trueNegatives'),
    key: 'trueNegatives',
    colorClass: 'metric-color-teal',
    tooltipTitle: t(
      'ai.review.groundTruthEvaluation.metrics.trueNegativesTooltipText'
    ),
  },
  {
    label: t('ai.review.groundTruthEvaluation.metrics.falseNegatives'),
    key: 'falseNegatives',
    colorClass: 'metric-color-yellow',
    tooltipTitle: t(
      'ai.review.groundTruthEvaluation.metrics.falseNegativesTooltipText'
    ),
  },
];

const metrics: {
  label: string;
  key: keyof IMetrics;
  tooltipTitle: string;
}[] = [
  {
    label: t('ai.review.groundTruthEvaluation.scores.accuracy'),
    key: 'accuracy',
    tooltipTitle: t(
      'ai.review.groundTruthEvaluation.scores.accuracyTooltipText'
    ),
  },
  {
    label: t('ai.review.groundTruthEvaluation.scores.precision'),
    key: 'precision',
    tooltipTitle: t(
      'ai.review.groundTruthEvaluation.scores.precisionTooltipText'
    ),
  },
  {
    label: t('ai.review.groundTruthEvaluation.scores.recall'),
    key: 'recall',
    tooltipTitle: t('ai.review.groundTruthEvaluation.scores.recallTooltipText'),
  },
  {
    label: t('ai.review.groundTruthEvaluation.scores.f1Score'),
    key: 'f1Score',
    tooltipTitle: t(
      'ai.review.groundTruthEvaluation.scores.f1ScoreTooltipText'
    ),
  },
];

const Divider = () => <Box className="ground-truth-eval-divider" />;

const GRID_SIZE = 2.9;

function getFormattedConfusionMatrix(num: number | undefined) {
  return !!num ? numberFormatter.format(num) : num;
}

function getFormattedMatrix(num: number | undefined) {
  return !!num ? `${num * 100}%` : num;
}

const GroundTruthEvaluation = ({jobId}: {jobId: string}) => {
  const {data: groundTruthData, isLoading: isGroundTruthLoading} =
    useGetGroundTruthEvaluation(jobId);
  return (
    <>
      {isGroundTruthLoading && (
        <ArrLoadingSpinner isOpen={isGroundTruthLoading} overlay={true} />
      )}
      <Box
        className="ground-truth-container"
        data-testid="ground-truth-container"
      >
        <Stack
          className="ground-truth-eval-selection-container"
          data-testid="ground-truth-box"
        >
          <Typography
            className="typo-text label2_size text-body-subtle ground-truth-eval-selection"
            tabIndex={0}
            data-testid={`ground-truth-evaluation-selection-label`}
          >
            {t('ai.review.groundTruthEvaluation.selectionLabel')}
          </Typography>
          <Typography
            className="typo-text subhead3_size highlight-text-secondary"
            tabIndex={0}
            data-testid={`ground-truth-evaluation-selection-value`}
          >
            {groundTruthData?.groundTruthName}
          </Typography>
        </Stack>
        <Typography
          className=" typo-text body3_size ground-truth-eval-desc"
          tabIndex={0}
          data-testid={`ground-truth-evaluation-description`}
        >
          {t('ai.review.groundTruthEvaluation.description')}
        </Typography>
        <Box className="ground-truth-eval-metrics">
          <Grid
            container
            spacing={1}
            className="ground-truth-eval-metrics-row"
            data-testid="ground-truth-box-wrapper"
          >
            {confusionMatrix.map((metric, idx) => (
              <Fragment key={metric.label}>
                <Grid
                  size={{xs: GRID_SIZE}}
                  className={`ground-truth-eval-metric ${metric.colorClass}${idx !== 0 ? ' with-divider' : ''}`}
                  tabIndex={0}
                  aria-label={`${groundTruthData?.confusionMatrix?.[metric.key]} ${metric.label}`}
                  data-testid="ground-truth-label-value"
                >
                  <Typography
                    className="typo-text subhead3_size ground-truth-eval-metric-value"
                    data-testid={`ground-truth-metric-${metric.label}`}
                    aria-hidden="true"
                  >
                    {getFormattedConfusionMatrix(
                      groundTruthData?.confusionMatrix?.[metric.key]
                    )}
                  </Typography>

                  <Typography
                    className="typo-text body4_size ground-truth-eval-metric-label"
                    data-testid={`ground-truth-evaluation-dummy-tooltip-text`}
                    aria-hidden="true"
                  >
                    {metric.label}
                    <ArrTooltipWrapper
                      trigger={
                        <InfoIcon
                          className="info-icon-label"
                          data-testid="toggle-info-tooltip"
                          aria-hidden="true"
                          role="button"
                          tabIndex={0}
                          aria-label="info icon"
                        />
                      }
                      title={metric.tooltipTitle}
                    >
                      {metric.tooltipTitle}
                    </ArrTooltipWrapper>
                  </Typography>
                </Grid>
                {idx === 1 && <Divider />}
              </Fragment>
            ))}
          </Grid>
          <Box className="ground-truth-eval-main-divider">
            <ArrowDivider aria-hidden="true" focusable="false" />
          </Box>
          <Grid
            container
            spacing={1}
            className="ground-truth-eval-metrics-row scores-row"
            data-testid="ground-truth-evaluation-dummy-metric-row"
          >
            {metrics.map((score, idx) => (
              <Grid
                size={{xs: GRID_SIZE}}
                key={score.label}
                className={`ground-truth-eval-score${idx !== 0 ? ' with-divider' : ''}`}
                tabIndex={0}
                aria-label={`${groundTruthData?.metrics?.[score.key]} ${score.label}`}
                data-testid="ground-truth-evaluation-dummy-metric-row-box"
              >
                <Typography
                  className="typo-text subhead3_size text-headings"
                  data-testid={`ground-truth-evaluation-score-value-${idx}`}
                  aria-hidden="true"
                >
                  {getFormattedMatrix(groundTruthData?.metrics?.[score.key])}
                </Typography>
                <Typography
                  className="typo-text body4_size text-body-strong"
                  data-testid={`ground-truth-evaluation-score-label-${idx}`}
                  aria-hidden="true"
                >
                  {score.label}
                  <ArrTooltipWrapper
                    trigger={
                      <InfoIcon
                        className="info-icon-label"
                        data-testid="toggle-info-tooltip"
                        tabIndex={0}
                        aria-label="info icon"
                        role="button"
                      />
                    }
                    title={score.tooltipTitle}
                  >
                    {score.tooltipTitle}
                  </ArrTooltipWrapper>
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default GroundTruthEvaluation;
